// useSpeedPointPlacer.js — When speed-edit mode is active, draws an invisible
// hit-area polyline over the lane so the user can click to place speed
// segment boundaries. Shows markers at existing breakpoints with mile labels.

import { useEffect, useRef } from 'react';

// ─── Haversine helpers ───────────────────────────────────────────────────────

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const METERS_TO_MILES = 0.000621371;

/** Compute cumulative mile distances for each waypoint. Returns array of miles. */
function buildCumulativeMiles(waypoints) {
  if (!waypoints?.length) return [];
  const cum = [0];
  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1];
    const b = waypoints[i];
    const segM = haversineMeters(a.lat, a.lng, b.lat, b.lng);
    cum.push(cum[i - 1] + segM * METERS_TO_MILES);
  }
  return cum;
}

/** Snap a lat/lng to the nearest point on the polyline and return its mile mark. */
function mileMarkOnPath(waypoints, cumulativeMiles, lat, lng) {
  let bestMile = 0;
  let bestDist = Infinity;

  for (let i = 0; i < waypoints.length - 1; i++) {
    const ax = waypoints[i].lat, ay = waypoints[i].lng;
    const bx = waypoints[i + 1].lat, by = waypoints[i + 1].lng;
    const abx = bx - ax, aby = by - ay;
    const len2 = abx * abx + aby * aby;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((lat - ax) * abx + (lng - ay) * aby) / len2)) : 0;
    const projLat = ax + t * abx;
    const projLng = ay + t * aby;
    const d = Math.hypot(projLat - lat, projLng - lng);
    if (d < bestDist) {
      bestDist = d;
      const segMiles = cumulativeMiles[i + 1] - cumulativeMiles[i];
      bestMile = cumulativeMiles[i] + t * segMiles;
    }
  }
  return bestMile;
}

/** Given a mile mark, find the lat/lng position on the polyline. */
function latLngAtMile(waypoints, cumulativeMiles, mile) {
  if (!waypoints?.length) return null;
  if (mile <= 0) return { lat: waypoints[0].lat, lng: waypoints[0].lng };
  for (let i = 0; i < waypoints.length - 1; i++) {
    if (mile <= cumulativeMiles[i + 1] || i === waypoints.length - 2) {
      const segMiles = cumulativeMiles[i + 1] - cumulativeMiles[i];
      const t = segMiles > 0 ? (mile - cumulativeMiles[i]) / segMiles : 0;
      return {
        lat: waypoints[i].lat + t * (waypoints[i + 1].lat - waypoints[i].lat),
        lng: waypoints[i].lng + t * (waypoints[i + 1].lng - waypoints[i].lng),
      };
    }
  }
  return { lat: waypoints[waypoints.length - 1].lat, lng: waypoints[waypoints.length - 1].lng };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useSpeedPointPlacer — click-to-place speed breakpoints on a lane.
 *
 * @param {Object} params
 * @param {google.maps.Map|null} params.map
 * @param {boolean} params.enabled            - speedEditMode active
 * @param {Array<{lat,lng}>} params.waypoints - active lane waypoints
 * @param {Array<{mileMark,speedMph}>} params.speedSegments - existing breakpoints
 * @param {Function} params.onBreakpointAdded - (mileMark: number) => void
 */
export function useSpeedPointPlacer({
  map,
  enabled,
  waypoints,
  speedSegments = [],
  onBreakpointAdded,
}) {
  const breakpointMarkersRef = useRef([]);
  const cleanupRef = useRef(null);
  // Keep a live ref to the callback so the click handler always uses the latest
  const onBreakpointAddedRef = useRef(onBreakpointAdded);
  onBreakpointAddedRef.current = onBreakpointAdded;

  // ── Hit area + hover tooltip + click handler ─────────────────────────────

  useEffect(() => {
    // Cleanup previous
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }

    if (!map || !window.google?.maps || !enabled || !waypoints?.length || waypoints.length < 2) {
      return;
    }

    const g = window.google.maps;
    const path = waypoints.map((wp) => ({ lat: wp.lat, lng: wp.lng }));
    const cumulativeMiles = buildCumulativeMiles(waypoints);

    // ── Invisible wide hit area ──
    const hitArea = new g.Polyline({
      path,
      strokeColor: '#b07878',
      strokeWeight: 26,
      strokeOpacity: 0,
      map,
      clickable: true,
      zIndex: 250,
    });

    // ── Lightweight DOM tooltip (pointer-events: none — never blocks clicks) ──
    const mapDiv = map.getDiv();
    const tooltip = document.createElement('div');
    tooltip.style.cssText =
      'position:fixed;pointer-events:none;z-index:9999;' +
      'background:rgba(15,23,42,0.88);color:#d4a0a0;' +
      'font-size:11px;font-weight:700;padding:2px 6px;border-radius:4px;' +
      'white-space:nowrap;display:none;transform:translate(-50%,-140%);';
    document.body.appendChild(tooltip);

    // Track latest computed mile for click handler
    let lastMile = null;

    // ── Event listeners ──
    const gListeners = [];

    // Native mousemove on the map div to position tooltip without blocking clicks
    function onNativeMouseMove(e) {
      if (tooltip.style.display === 'block') {
        tooltip.style.left = `${e.clientX}px`;
        tooltip.style.top = `${e.clientY}px`;
      }
    }
    mapDiv.addEventListener('mousemove', onNativeMouseMove);

    gListeners.push(g.event.addListener(hitArea, 'mousemove', (e) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null) return;
      const mile = mileMarkOnPath(waypoints, cumulativeMiles, lat, lng);
      lastMile = mile;
      tooltip.textContent = `${mile.toFixed(2)} mi — click to set`;
      tooltip.style.display = 'block';
    }));

    gListeners.push(g.event.addListener(hitArea, 'mouseout', () => {
      tooltip.style.display = 'none';
      lastMile = null;
    }));

    gListeners.push(g.event.addListener(hitArea, 'click', (e) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null) return;
      const mile = mileMarkOnPath(waypoints, cumulativeMiles, lat, lng);
      onBreakpointAddedRef.current?.(mile);
    }));

    // Also listen to map clicks — if the user clicks near the line but misses
    // the polyline hit area, use the native click's latLng
    gListeners.push(g.event.addListener(map, 'click', (e) => {
      // Only handle if we had a recent hover (user was near the line)
      if (lastMile != null) {
        onBreakpointAddedRef.current?.(lastMile);
        lastMile = null;
      }
    }));

    // ── Cleanup function ──
    const cleanup = () => {
      gListeners.forEach((l) => g.event.removeListener(l));
      mapDiv.removeEventListener('mousemove', onNativeMouseMove);
      hitArea.setMap(null);
      tooltip.style.display = 'none';
      if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
    };
    cleanupRef.current = cleanup;

    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, waypoints]);

  // ── Breakpoint markers — show dots at existing speed breakpoint locations ──

  useEffect(() => {
    // Clear old
    breakpointMarkersRef.current.forEach((m) => m.setMap(null));
    breakpointMarkersRef.current = [];

    if (!map || !window.google?.maps || !enabled || !waypoints?.length || waypoints.length < 2) return;
    if (!speedSegments?.length) return;

    const g = window.google.maps;
    const cumulativeMiles = buildCumulativeMiles(waypoints);

    speedSegments.forEach((seg, idx) => {
      const pos = latLngAtMile(waypoints, cumulativeMiles, seg.mileMark);
      if (!pos) return;

      const marker = new g.Marker({
        position: pos,
        map,
        zIndex: 225,
        clickable: false,       // Don't intercept clicks — let them pass through to hit area
        icon: {
          path: g.SymbolPath.CIRCLE,
          scale: idx === 0 ? 8 : 6,
          fillColor: idx === 0 ? '#c48888' : '#b07878',
          fillOpacity: 1,
          strokeColor: '#0f172a',
          strokeWeight: 2,
        },
        label: {
          text: `${seg.speedMph}`,
          color: '#ffffff',
          fontSize: '10px',
          fontWeight: '700',
        },
        title: `${seg.mileMark.toFixed(2)} mi — ${seg.speedMph} mph`,
      });

      breakpointMarkersRef.current.push(marker);
    });

    return () => {
      breakpointMarkersRef.current.forEach((m) => m.setMap(null));
      breakpointMarkersRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, waypoints, speedSegments]);
}
